import React, { useEffect, useState } from 'react';
import { periodApi, CreatePeriodData, UpdatePeriodData } from '../services/periodApi';
import { Period } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import { handleApiError, showError, showSuccess } from '../utils/handleApiError';
import ConfirmModal from '../components/ConfirmModal';

const Periods: React.FC = () => {
  const { t } = useLanguage();
  const [periods, setPeriods] = useState<Period[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingPeriod, setEditingPeriod] = useState<Period | null>(null);
  const [activePeriod, setActivePeriod] = useState<Period | null>(null);
  const [confirmAction, setConfirmAction] = useState<{ action: 'delete' | 'close'; id: number } | null>(null);
  const [newPeriod, setNewPeriod] = useState<Partial<CreatePeriodData>>({
    name: '',
    type: 'monthly',
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    description: '',
  });

  useEffect(() => {
    loadPeriods();
    loadActivePeriod();
  }, []);

  const loadPeriods = async () => {
    try {
      setLoading(true);
      const data = await periodApi.getPeriods();
      setPeriods(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading periods:', error);
      handleApiError(error, 'Hiba az időszakok betöltése során');
      setPeriods([]);
    } finally {
      setLoading(false);
    }
  };

  const loadActivePeriod = async () => {
    try {
      const period = await periodApi.getActive();
      setActivePeriod(period);
    } catch (error) {
      // No active period is okay
      setActivePeriod(null);
    }
  };

  const handleAddPeriod = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const periodData: CreatePeriodData = {
        name: newPeriod.name || '',
        type: newPeriod.type as 'monthly' | 'quarterly' | 'yearly' | 'custom',
        start_date: newPeriod.start_date || '',
        end_date: newPeriod.end_date || '',
        description: newPeriod.description,
      };

      if (new Date(periodData.end_date) <= new Date(periodData.start_date)) {
        showError('A záró dátumnak a kezdő dátum után kell lennie');
        return;
      }

      await periodApi.createPeriod(periodData);
      showSuccess('Időszak sikeresen létrehozva');
      setShowAddForm(false);
      setNewPeriod({
        name: '',
        type: 'monthly',
        start_date: new Date().toISOString().split('T')[0],
        end_date: new Date().toISOString().split('T')[0],
        description: '',
      });
      loadPeriods();
    } catch (error) {
      console.error('Error creating period:', error);
      handleApiError(error, 'Hiba az időszak létrehozása során');
    }
  };

  const handleEditPeriod = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingPeriod) return;

    try {
      const periodData: UpdatePeriodData = {
        name: editingPeriod.name,
        type: editingPeriod.type,
        start_date: editingPeriod.start_date,
        end_date: editingPeriod.end_date,
        description: editingPeriod.description,
      };

      if (new Date(periodData.end_date!) <= new Date(periodData.start_date!)) {
        showError('A záró dátumnak a kezdő dátum után kell lennie');
        return;
      }

      await periodApi.updatePeriod(editingPeriod.id, periodData);
      showSuccess('Időszak sikeresen frissítve');
      setShowEditForm(false);
      setEditingPeriod(null);
      loadPeriods();
    } catch (error: any) {
      console.error('Error updating period:', error);
      if (error.response?.status === 403) {
        showError('Lezárt időszak nem módosítható');
      } else {
        handleApiError(error, 'Hiba az időszak frissítése során');
      }
    }
  };

  const handleDeletePeriod = async (id: number) => {
    try {
      await periodApi.deletePeriod(id);
      showSuccess('Időszak sikeresen törölve');
      loadPeriods();
    } catch (error: any) {
      console.error('Error deleting period:', error);
      if (error.response?.status === 403) {
        showError('Lezárt időszak nem törölhető');
      } else if (error.response?.status === 400) {
        showError('Az időszakhoz tartoznak költségek, nem törölhető');
      } else {
        handleApiError(error, 'Hiba az időszak törlése során');
      }
    }
  };

  const handleSetActive = async (id: number) => {
    try {
      const result = await periodApi.setActive(id);
      showSuccess(result.message);
      setActivePeriod(result.period);
      loadPeriods();
    } catch (error) {
      console.error('Error setting active period:', error);
      handleApiError(error, 'Hiba az időszak aktiválása során');
    }
  };

  const handleClosePeriod = async (id: number) => {
    try {
      const result = await periodApi.close(id);
      showSuccess(result.message);
      if (activePeriod?.id === id) {
        setActivePeriod(null);
      }
      loadPeriods();
    } catch (error) {
      console.error('Error closing period:', error);
      handleApiError(error, 'Hiba az időszak lezárása során');
    }
  };

  const handleConfirmAction = async () => {
    if (!confirmAction) return;
    const { action, id } = confirmAction;

    try {
      if (action === 'delete') await handleDeletePeriod(id);
      else if (action === 'close') await handleClosePeriod(id);
    } finally {
      setConfirmAction(null);
    }
  };

  const handleReopenPeriod = async (id: number) => {
    try {
      const result = await periodApi.reopen(id);
      showSuccess(result.message);
      loadPeriods();
    } catch (error) {
      console.error('Error reopening period:', error);
      handleApiError(error, 'Hiba az időszak újranyitása során');
    }
  };

  const getPeriodTypeName = (type: string) => {
    const types: Record<string, string> = {
      monthly: 'Havi',
      quarterly: 'Negyedéves',
      yearly: 'Éves',
      custom: 'Egyedi',
    };
    return types[type] || type;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('hu-HU');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg">Betöltés...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Időszakok kezelése</h1>
        <button
          onClick={() => setShowAddForm(true)}
          className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
        >
          Új időszak
        </button>
      </div>

      {activePeriod && (
        <div className="bg-green-50 border-l-4 border-green-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-700">
                <span className="font-medium">Aktív időszak: </span>
                {activePeriod.name} ({formatDate(activePeriod.start_date)} - {formatDate(activePeriod.end_date)})
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Név
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Típus
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Kezdő dátum
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Záró dátum
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Státusz
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Műveletek
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {periods.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                  Nincs még időszak
                </td>
              </tr>
            ) : (
              periods.map((period) => (
                <tr key={period.id} className={period.is_active ? 'bg-green-50' : ''}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{period.name}</div>
                    {period.description && (
                      <div className="text-sm text-gray-500">{period.description}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {getPeriodTypeName(period.type)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(period.start_date)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(period.end_date)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col gap-1">
                      {period.is_active && (
                        <span className="inline-flex px-2 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          Aktív
                        </span>
                      )}
                      {period.is_closed && (
                        <span className="inline-flex px-2 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                          Lezárva
                        </span>
                      )}
                      {!period.is_active && !period.is_closed && (
                        <span className="inline-flex px-2 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          Inaktív
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end gap-2">
                      {!period.is_active && !period.is_closed && (
                        <button
                          onClick={() => handleSetActive(period.id)}
                          className="text-green-600 hover:text-green-900"
                          title="Aktiválás"
                        >
                          Aktiválás
                        </button>
                      )}
                      {!period.is_closed && (
                        <>
                          <button
                            onClick={() => {
                              setEditingPeriod(period);
                              setShowEditForm(true);
                            }}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            Szerkesztés
                          </button>
                          <button
                            onClick={() => setConfirmAction({ action: 'close', id: period.id })}
                            className="text-orange-600 hover:text-orange-900"
                          >
                            Lezárás
                          </button>
                        </>
                      )}
                      {period.is_closed && (
                        <button
                          onClick={() => handleReopenPeriod(period.id)}
                          className="text-orange-600 hover:text-orange-900"
                        >
                          Újranyitás
                        </button>
                      )}
                      {!period.is_closed && (
                        <button
                          onClick={() => setConfirmAction({ action: 'delete', id: period.id })}
                          className="text-red-600 hover:text-red-900"
                        >
                          Törlés
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add Period Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Új időszak létrehozása</h3>
              <button
                onClick={() => setShowAddForm(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <span className="text-2xl">&times;</span>
              </button>
            </div>

            <form onSubmit={handleAddPeriod} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Név *
                </label>
                <input
                  type="text"
                  required
                  value={newPeriod.name}
                  onChange={(e) => setNewPeriod({ ...newPeriod, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="pl. 2024 Január, 2024 Q1, stb."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Típus *
                </label>
                <select
                  required
                  value={newPeriod.type}
                  onChange={(e) => setNewPeriod({ ...newPeriod, type: e.target.value as any })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="monthly">Havi</option>
                  <option value="quarterly">Negyedéves</option>
                  <option value="yearly">Éves</option>
                  <option value="custom">Egyedi</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Kezdő dátum *
                  </label>
                  <input
                    type="date"
                    required
                    value={newPeriod.start_date}
                    onChange={(e) => setNewPeriod({ ...newPeriod, start_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Záró dátum *
                  </label>
                  <input
                    type="date"
                    required
                    value={newPeriod.end_date}
                    onChange={(e) => setNewPeriod({ ...newPeriod, end_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Leírás
                </label>
                <textarea
                  value={newPeriod.description}
                  onChange={(e) => setNewPeriod({ ...newPeriod, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  rows={3}
                  placeholder="Opcionális megjegyzés az időszakról"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Mégse
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                  Létrehozás
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Period Modal */}
      {showEditForm && editingPeriod && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Időszak szerkesztése</h3>
              <button
                onClick={() => {
                  setShowEditForm(false);
                  setEditingPeriod(null);
                }}
                className="text-gray-400 hover:text-gray-500"
              >
                <span className="text-2xl">&times;</span>
              </button>
            </div>

            <form onSubmit={handleEditPeriod} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Név *
                </label>
                <input
                  type="text"
                  required
                  value={editingPeriod.name}
                  onChange={(e) => setEditingPeriod({ ...editingPeriod, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Típus *
                </label>
                <select
                  required
                  value={editingPeriod.type}
                  onChange={(e) => setEditingPeriod({ ...editingPeriod, type: e.target.value as any })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="monthly">Havi</option>
                  <option value="quarterly">Negyedéves</option>
                  <option value="yearly">Éves</option>
                  <option value="custom">Egyedi</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Kezdő dátum *
                  </label>
                  <input
                    type="date"
                    required
                    value={editingPeriod.start_date}
                    onChange={(e) => setEditingPeriod({ ...editingPeriod, start_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Záró dátum *
                  </label>
                  <input
                    type="date"
                    required
                    value={editingPeriod.end_date}
                    onChange={(e) => setEditingPeriod({ ...editingPeriod, end_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Leírás
                </label>
                <textarea
                  value={editingPeriod.description || ''}
                  onChange={(e) => setEditingPeriod({ ...editingPeriod, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  rows={3}
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditForm(false);
                    setEditingPeriod(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Mégse
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                  Mentés
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <ConfirmModal
        isOpen={confirmAction !== null}
        onConfirm={handleConfirmAction}
        onCancel={() => setConfirmAction(null)}
        title={confirmAction?.action === 'delete' ? 'Időszak törlése' : 'Időszak lezárása'}
        message={confirmAction?.action === 'delete'
          ? 'Biztosan törölni szeretnéd ezt az időszakot?'
          : 'Biztosan le szeretnéd zárni ezt az időszakot? Lezárt időszak nem módosítható!'
        }
        confirmLabel={confirmAction?.action === 'delete' ? 'Törlés' : 'Lezárás'}
        variant={confirmAction?.action === 'delete' ? 'danger' : 'warning'}
      />
    </div>
  );
};

export default Periods;
